import { Dialog } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import PropTypes from "prop-types";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { Modal } from "../Modal";
import {
  filterCommandPaletteOptions,
  getCommandPaletteCompletion,
  getCommandPaletteShortcutLabel,
  getOptionText,
  hasPointerMoved,
  isApplePlatform,
  isCompletionOfQuery,
  isCommandPaletteShortcut,
} from "./commandPaletteUtils";
import {
  buildSearchResults,
  parseSearchModulePrefix,
  searchModules,
} from "./commandPaletteSearch";
import { useCommandPaletteSearch } from "./useCommandPaletteSearch";

const SearchResultThumbnail = ({ imageUrl }) => {
  const [failed, setFailed] = useState(false);
  return imageUrl && !failed ? (
    <img
      src={imageUrl}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-10 w-10 flex-shrink-0 rounded bg-gray-100 object-contain"
    />
  ) : (
    <span className="h-10 w-10 flex-shrink-0 rounded bg-gray-100" />
  );
};

SearchResultThumbnail.propTypes = {
  imageUrl: PropTypes.string,
};

// Matches the tag colours in the invoice detail page header.
const searchResultTagClassNames = {
  Approved: "border-yellow-800 bg-yellow-100 text-yellow-800",
  Exported: "border-green-800 bg-green-100 text-green-800",
  Paid: "border-red-800 bg-rose-100 text-red-800",
};

const SearchResultLabel = ({
  label,
  typeLabel,
  details = [],
  tags = [],
  imageUrl,
  showThumbnail,
}) => (
  <span className="flex w-full min-w-0 items-center gap-3">
    {showThumbnail && <SearchResultThumbnail imageUrl={imageUrl} />}
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="flex items-center gap-2">
        <span className="font-medium">{label}</span>
        {tags.map(tag => (
          <span
            key={tag}
            className={`rounded-full border px-1.5 text-[10px] font-bold uppercase leading-4 tracking-wide ${searchResultTagClassNames[tag] || ""}`}
          >
            {tag}
          </span>
        ))}
        <span className="ml-auto pl-4 text-xs text-gray-500">{typeLabel}</span>
      </span>
      {details.length > 0 && (
        <span className="truncate text-xs text-gray-500">
          {details.join(" · ")}
        </span>
      )}
    </span>
  </span>
);

SearchResultLabel.propTypes = {
  label: PropTypes.string.isRequired,
  typeLabel: PropTypes.string.isRequired,
  details: PropTypes.arrayOf(PropTypes.string),
  tags: PropTypes.arrayOf(PropTypes.string),
  imageUrl: PropTypes.string,
  showThumbnail: PropTypes.bool,
};

const buildSearchResultEntries = (results, firstIndex) =>
  buildSearchResults(results).map((result, position) => ({
    key: result.key,
    index: firstIndex + position,
    text: [result.label, ...result.tags, result.typeLabel, ...result.details]
      .filter(Boolean)
      .join(" "),
    option: {
      label: <SearchResultLabel {...result} />,
      href: result.href,
    },
  }));

const openOptionLink = option =>
  option.target === "_blank"
    ? window.open(option.href, "_blank", "noopener,noreferrer")
    : window.location.assign(option.href);

const CommandPalette = ({ options = [], title = "Actions" }) => {
  const applePlatform = useMemo(() => isApplePlatform(), []);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchModule, setSearchModule] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeModalText, setActiveModalText] = useState(null);
  const inputReference = useRef(null);
  const listReference = useRef(null);
  const pointerPositionReference = useRef(null);
  const listId = useId();

  const search = useCommandPaletteSearch({
    query: isOpen ? query : "",
    searchModule,
  });
  const actionEntries = searchModule
    ? []
    : filterCommandPaletteOptions(options, query);
  const entries = [
    ...buildSearchResultEntries(search.results, options.length),
    ...actionEntries,
  ];
  const completion = getCommandPaletteCompletion(actionEntries, query);
  const activeModalOption = activeModalText
    ? options.find(option => getOptionText(option) === activeModalText)
    : null;

  useEffect(() => {
    // Capture phase, because Modal stops keydown propagation inside dialogs.
    const onKeyDown = event => {
      if (
        event.defaultPrevented ||
        !isCommandPaletteShortcut(event, applePlatform)
      )
        return;
      event.preventDefault();
      if (event.repeat || activeModalText) return;
      if (isOpen) {
        setIsOpen(false);
        return;
      }
      if (document.querySelector('[role="dialog"]')) return;
      setQuery("");
      setSearchModule(null);
      setActiveIndex(0);
      pointerPositionReference.current = null;
      setIsOpen(true);
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [applePlatform, activeModalText, isOpen]);

  useEffect(() => {
    listReference.current?.children?.[activeIndex]?.scrollIntoView?.({
      block: "nearest",
    });
  }, [activeIndex]);

  const runEntry = entry => {
    const { option } = entry || {};
    if (!option || option.disabled) return;
    setIsOpen(false);
    if (option.modalProps) setActiveModalText(entry.text);
    else if (option.href) openOptionLink(option);
    else option.onClick?.();
  };

  const updateQuery = nextQuery => {
    const modulePrefix = searchModule
      ? null
      : parseSearchModulePrefix(nextQuery);
    if (modulePrefix) setSearchModule(modulePrefix.searchModule);
    setQuery(modulePrefix ? modulePrefix.rest : nextQuery);
    setActiveIndex(0);
  };

  const clearSearchModule = () => {
    setSearchModule(null);
    setActiveIndex(0);
    inputReference.current?.focus();
  };

  const activateOnPointerMove = (event, position) => {
    const pointerPosition = { x: event.screenX, y: event.screenY };
    const moved = hasPointerMoved(
      pointerPositionReference.current,
      pointerPosition,
    );
    pointerPositionReference.current = pointerPosition;
    if (moved) setActiveIndex(position);
  };

  const moveActiveIndex = step =>
    setActiveIndex(current =>
      entries.length ? (current + step + entries.length) % entries.length : 0,
    );

  const onInputKeyDown = event => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      moveActiveIndex(event.key === "ArrowDown" ? 1 : -1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      runEntry(entries[activeIndex]);
    } else if (event.key === "Tab" && completion) {
      event.preventDefault();
      updateQuery(completion);
    } else if (event.key === "Backspace" && !query && searchModule) {
      event.preventDefault();
      clearSearchModule();
    } else if (event.key === "Escape") {
      event.preventDefault();
      if (query) updateQuery("");
      else if (searchModule) clearSearchModule();
      else setIsOpen(false);
    }
  };

  const closeActiveModal = () => {
    activeModalOption?.modalProps?.onClose?.();
    setActiveModalText(null);
  };

  const hasActions = options.length > 0;
  const titleText = typeof title === "string" ? title.toLowerCase() : "actions";
  const placeholder = searchModule
    ? `Search ${searchModule.label.toLowerCase()}…`
    : hasActions
      ? `Search ${titleText}…`
      : "Search…";
  const emptyMessage = searchModule
    ? search.loading
      ? `Searching ${searchModule.label.toLowerCase()}…`
      : query.trim()
        ? `No matching ${searchModule.label.toLowerCase()}`
        : `Type to search ${searchModule.label.toLowerCase()}`
    : search.loading
      ? "Searching references…"
      : hasActions
        ? `No matching ${titleText}`
        : "No matching results";

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        initialFocus={inputReference}
        className="fixed inset-0 z-50 overflow-y-auto p-4 pt-[15vh]"
      >
        <div
          className="fixed inset-0 bg-gray-300 opacity-75"
          aria-hidden="true"
        />
        <Dialog.Panel
          className="relative mx-auto max-w-xl overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5"
          onKeyDown={event => event.stopPropagation()}
        >
          <Dialog.Title className="sr-only">{title}</Dialog.Title>
          <div className="relative flex items-center border-b border-gray-100">
            <MagnifyingGlassIcon
              className="pointer-events-none ml-4 h-5 w-5 flex-shrink-0 text-gray-400"
              aria-hidden="true"
            />
            {searchModule && (
              <span
                data-testid="command-palette-module"
                className="relative z-10 ml-2 inline-flex flex-shrink-0 items-center gap-1 rounded-md bg-gray-900 py-0.5 pl-2 pr-1 text-xs font-medium text-white"
              >
                {searchModule.label}
                <button
                  type="button"
                  aria-label={`Stop searching ${searchModule.label.toLowerCase()}`}
                  onClick={clearSearchModule}
                  className="rounded px-1 text-gray-300 hover:text-white"
                >
                  ×
                </button>
              </span>
            )}
            {completion && isCompletionOfQuery(completion, query) && (
              <div
                aria-hidden="true"
                data-testid="command-palette-completion"
                className="pointer-events-none absolute inset-0 flex items-center whitespace-pre pl-11 pr-4 text-sm text-gray-400"
              >
                <span className="invisible">{query}</span>
                {completion.slice(query.length)}
              </div>
            )}
            {completion && !isCompletionOfQuery(completion, query) && (
              <div
                aria-hidden="true"
                data-testid="command-palette-completion"
                className="pointer-events-none absolute right-4 z-10 max-w-[50%] truncate text-xs text-gray-400"
              >
                Tab → {completion}
              </div>
            )}
            <input
              ref={inputReference}
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="both"
              aria-activedescendant={
                entries[activeIndex] ? `${listId}-${activeIndex}` : undefined
              }
              autoComplete="off"
              spellCheck={false}
              placeholder={placeholder}
              value={query}
              onChange={event => updateQuery(event.target.value)}
              onKeyDown={onInputKeyDown}
              className="relative h-12 min-w-0 flex-1 border-0 bg-transparent pl-2 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
            />
          </div>
          {entries.length > 0 ? (
            <ul
              id={listId}
              ref={listReference}
              role="listbox"
              aria-label={title}
              className="max-h-80 overflow-y-auto p-1"
            >
              {entries.map((entry, position) => (
                <li
                  key={entry.key || `${entry.index}-${entry.text}`}
                  id={`${listId}-${position}`}
                  role="option"
                  aria-selected={position === activeIndex}
                  aria-disabled={Boolean(entry.option.disabled)}
                  title={entry.text}
                  onMouseMove={event => activateOnPointerMove(event, position)}
                  onClick={() => runEntry(entry)}
                  className={`flex cursor-default select-none items-center truncate whitespace-nowrap rounded-md px-3 py-2 text-sm ${
                    entry.option.disabled ? "text-gray-400" : "text-gray-900"
                  } ${position === activeIndex ? "bg-gray-100" : ""}`}
                >
                  {entry.option.label}
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-4 py-6 text-center text-sm text-gray-500">
              {emptyMessage}
            </p>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-500">
            <span>↑↓ to navigate</span>
            <span>Enter to run</span>
            {searchModule ? (
              <span>Backspace to leave {searchModule.label.toLowerCase()}</span>
            ) : (
              <span>Tab to complete</span>
            )}
            <span>
              {query || searchModule ? "Esc to clear" : "Esc to close"}
            </span>
            {!searchModule && (
              <span title="Type a prefix then a space to search one module">
                {searchModules
                  .map(({ prefix, label }) => `${prefix} ${label}`)
                  .join(" · ")}
              </span>
            )}
            <span className="ml-auto">
              {getCommandPaletteShortcutLabel(applePlatform)}
            </span>
          </div>
        </Dialog.Panel>
      </Dialog>
      {activeModalOption?.modalProps && (
        <Modal
          {...activeModalOption.modalProps}
          isOpen
          onClose={closeActiveModal}
        />
      )}
    </>
  );
};

CommandPalette.propTypes = {
  options: PropTypes.array,
  title: PropTypes.string,
};

export { CommandPalette };
